import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createHtmlInsight,
  deleteInsight,
  fetchAllInsights,
  fetchInsightHtml,
  fetchInsightReports,
  renameInsight,
} from "@/services/DataService";
import type { HtmlInsightCreatePayload, Insight } from "@/services/DataService";

export const useInsights = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["insights"],
    queryFn: fetchAllInsights,
    enabled: options?.enabled ?? true,
  });
};

export const useInsightReports = (insightId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["insightReports", insightId],
    queryFn: () => fetchInsightReports(insightId),
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Fetch the raw HTML body of an insight (Report Studio code view).
 * Kept separate from `useInsights` because the body can be large and we
 * don't want it loaded by every insight-listing surface.
 */
export const useInsightHtml = (insightId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["insightHtml", insightId],
    queryFn: () => fetchInsightHtml(insightId),
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Mutation that creates a pre-rendered HTML insight (server stores it in
 * `READY` state, no workflow triggered). Used by the Reports "Create a
 * report" flow to spin up a bare scaffold so the Report Studio has a real
 * insight id from the very first render — the chat agent then edits the
 * file in the sandbox and the studio refetches the rendered HTML on each
 * tool turn.
 *
 * On success the cached `useInsights()` list is invalidated so the new
 * insight shows up immediately in any reports list rendered alongside.
 */
export const useCreateHtmlInsight = () => {
  const queryClient = useQueryClient();
  return useMutation<Insight, Error, HtmlInsightCreatePayload>({
    mutationKey: ["createHtmlInsight"],
    mutationFn: (payload) => createHtmlInsight(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

/**
 * Rename a custom (HTML) report. On success the `useInsights()` list is
 * invalidated so the new title shows up everywhere the reports list is
 * rendered (Reports page + report viewer breadcrumb).
 */
export const useRenameInsight = () => {
  const queryClient = useQueryClient();
  return useMutation<Insight, Error, { id: string; displayName: string }>({
    mutationKey: ["renameInsight"],
    mutationFn: ({ id, displayName }) => renameInsight(id, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};

/**
 * Delete a custom (HTML) report. On success the `useInsights()` list is
 * invalidated so the removed report disappears from every reports list.
 */
export const useDeleteInsight = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationKey: ["deleteInsight"],
    mutationFn: (id) => deleteInsight(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });
};
